// contexts/IdleTimerContext.tsx
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useIdleTimer } from "react-idle-timer";

interface IdleTimerContextType {
  isIdle: boolean;
  showModal: boolean;
  countdown: number;
  dismissModal: () => void;
}

const IdleTimerContext = createContext<IdleTimerContextType | undefined>(
  undefined
);

interface IdleTimerProviderProps {
  children: ReactNode;
  onLogout?: () => void;
  timeout?: number; 
  modalTimeout?: number;
}

export const IdleTimerProvider: React.FC<IdleTimerProviderProps> = ({
  children,
  onLogout,
  timeout = 4 * 60* 60*1000, // 4 housr
  modalTimeout = 60,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [countdown, setCountdown] = useState(modalTimeout);

  const handleIdle = () => {
    setShowModal(true);
    setCountdown(modalTimeout);
  };

  const handleActive = () => {
  };

  const { isIdle, reset } = useIdleTimer({
    onIdle: handleIdle,
    onActive: handleActive,
    timeout,
    throttle: 500,
    crossTab: true,
    leaderElection: true,
    stopOnIdle: true,
  });

  useEffect(() => {
    if (!showModal) return;

    if (countdown <= 0) {
      setShowModal(false);
      onLogout?.();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [showModal, countdown, onLogout]);

  const dismissModal = () => {
    setShowModal(false);
    setCountdown(modalTimeout);
    reset();
  };

  const contextValue: IdleTimerContextType = {
    isIdle: isIdle(),
    showModal,
    countdown,
    dismissModal,
  };

  return (
    <IdleTimerContext.Provider value={contextValue}>
      {children}
    </IdleTimerContext.Provider>
  );
};

export const useIdleTimerContext = (): IdleTimerContextType => {
  const context = useContext(IdleTimerContext);
  if (!context) {
    throw new Error(
      "useIdleTimerContext must be used within IdleTimerProvider"
    );
  }
  return context;
};
